import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Select, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmptyState from '../components/EmptyState';

const { Title } = Typography;
const { Option } = Select;

interface Content {
  id: string;
  website_id: string;
  type: 'page' | 'post' | 'product';
  title: string;
  slug: string;
  published: boolean;
  updated_at: string;
}

interface Website {
  id: string;
  domain: string;
}

const ContentList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [contents, setContents] = useState<Content[]>([]);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  const fetchContents = async (page = 1, websiteId = selectedWebsite, type = selectedType) => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/contents?page=${page}&pageSize=${pagination.pageSize}`;
      
      if (websiteId) {
        url += `&website=${websiteId}`;
      }
      
      if (type) {
        url += `&type=${type}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        setContents(response.data.data);
        setPagination({
          ...pagination,
          current: page,
          total: response.data.pagination.total,
        });
      } else {
        message.error(response.data.message || '获取内容列表失败');
      }
    } catch (error: any) {
      message.error('获取内容列表时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo`);
      if (response.data.success) {
        setWebsites(response.data.data);
      }
    } catch (error: any) {
      message.error('获取网站列表时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    fetchWebsites();
    fetchContents();
  }, []);

  const handleTableChange = (pagination: any) => {
    fetchContents(pagination.current);
  };

  const handleWebsiteChange = (value: string) => {
    setSelectedWebsite(value);
    fetchContents(1, value, selectedType);
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    fetchContents(1, selectedWebsite, value);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/contents/${id}`);
      if (response.data.success) {
        message.success('内容删除成功');
        fetchContents(pagination.current);
      } else {
        message.error(response.data.message || '删除内容失败');
      }
    } catch (error: any) {
      message.error('删除内容时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  const typeLabels = {
    page: '页面',
    post: '文章',
    product: '产品'
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => (
        <Tag color={text === 'page' ? 'blue' : text === 'post' ? 'green' : 'orange'}>
          {typeLabels[text as keyof typeof typeLabels]}
        </Tag>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: '状态',
      dataIndex: 'published',
      key: 'published',
      render: (published: boolean) => (
        <Tag color={published ? 'green' : 'red'}>
          {published ? '已发布' : '草稿'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Content) => (
        <Space size="middle" className="table-actions">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => window.open(`/api/contents/${record.id}`, '_blank')}
          >
            预览
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/contents/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此内容吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (websites.length === 0 && !loading) {
    return (
      <EmptyState
        title="未找到网站"
        description="请先创建一个网站，然后再添加内容"
        createPath="/websites/new"
        createText="创建网站"
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>内容管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/contents/new')}
        >
          添加内容
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="选择网站"
            allowClear
            onChange={handleWebsiteChange}
          >
            {websites.map(website => (
              <Option key={website.id} value={website.id}>{website.domain}</Option>
            ))}
          </Select>
          
          <Select
            style={{ width: 120 }}
            placeholder="内容类型"
            allowClear
            onChange={handleTypeChange}
          >
            <Option value="page">页面</Option>
            <Option value="post">文章</Option>
            <Option value="product">产品</Option>
          </Select>
        </Space>
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={contents}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ContentList; 